import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { GridColDef } from "@mui/x-data-grid";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import DataGrids from "./DataGrid";
import { GET_EVENT_LIST, GET_EVENT_BOOKINGS } from "../graphql/query";
import type { EventItem, GetEventListResponse } from "../interfaces/event.interface";
import type { GetEventBookingsResponse } from "../interfaces/eventBooking.interface";
import { formatTo12Hour } from "../utils/time";
import themestyle from "../theme";

const formatDate = (value?: string) => {
  if (!value) return "-";
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[2]}/${match[3]}/${match[1]}`;
  return value;
};

type AllEventsTableProps = {
  onView: (id: string) => void;
  onDelete: (id: string, name: string) => void;
  onReport: (id: string) => void;
  refreshTrigger?: number;
};

const AllEventsTable: React.FC<AllEventsTableProps> = ({
  onView,
  onDelete,
  onReport,
  refreshTrigger,
}) => {
  const [inputSearch, setInputSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 5 });

  // Debounce: only send search to backend 400ms after the user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(inputSearch);
      // Reset to first page on new search
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [inputSearch]);

  const { data, loading, refetch, previousData } = useQuery<GetEventListResponse>(
    GET_EVENT_LIST,
    {
      variables: {
        page: paginationModel.page + 1,
        perPage: paginationModel.pageSize,
        search: searchQuery || "",
      },
      fetchPolicy: "cache-and-network",
    }
  );

  useEffect(() => {
    if (refreshTrigger) refetch();
  }, [refreshTrigger, refetch]);

  const rows = useMemo(() => {
    const list: EventItem[] = data?.getEventList?.data ?? [];
    return list.map((row, index) => ({
      ...row,
      serial: String(
        paginationModel.page * paginationModel.pageSize + index + 1
      ).padStart(2, "0"),
      dateRange: `${formatDate(row.startDate)} To ${formatDate(row.endDate)}`,
      priceLabel: `₹${Number(row.standardPrice || 0).toFixed(2)}`,
      discountAmountLabel: row.offers?.length
        ? row.offers
            .map((o) => {
              const standard = Number(row.standardPrice || 0);
              const offerPrice = Number(o.offerPrice || 0);
              const discount = Math.max(0, standard - offerPrice);
              return `₹${discount.toFixed(2)}`;
            })
            .join(", ")
        : "-",
      instructorLabel: row.instructors?.join(", ") || "-",
      displayTime: row.time ? row.time.split("-").map(t => formatTo12Hour(t.trim())).join(" - ") : "-",
    }));
  }, [data, paginationModel.page, paginationModel.pageSize]);

  const rowCount =
    data?.getEventList?.totalCount ?? previousData?.getEventList?.totalCount ?? 0;

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: "serial", headerName: "S.No", minWidth: 80, flex: 0.5, sortable: false },
      { field: "eventName", headerName: "Event Name", minWidth: 200, flex: 1.2 },
      { field: "location", headerName: "Location", minWidth: 150, flex: 0.9 },
      { field: "dateRange", headerName: "Date Range", minWidth: 230, flex: 1.2 },
      { field: "displayTime", headerName: "Time", minWidth: 180, flex: 0.7 },
      { field: "priceLabel", headerName: "Price", minWidth: 100, flex: 0.7 },
      {
        field: "discountAmountLabel",
        headerName: "Discount Amount",
        minWidth: 140,
        flex: 0.8,
      },
      {
        field: "isOfferActive",
        headerName: "Offer",
        minWidth: 110,
        flex: 0.6,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              height: "100%",
              pl: 0,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: params.row.isOfferActive
                  ? themestyle.colors.success
                  : themestyle.colors.error,
              }}
            />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                fontFamily: "Roboto",
                color: params.row.isOfferActive
                  ? themestyle.colors.success
                  : themestyle.colors.error,
              }}
            >
              {params.row.isOfferActive ? "Active" : "Inactive"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 160,
        flex: 0.9,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <IconButton
              size="small"
              onClick={() => onReport(params.row.id)}
              title="View Report"
              sx={{
                border: "1px solid #C7D9F5",
                color: "#1565C0",
                width: 28,
                height: 28,
              }}
            >
              <AssessmentOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onView(params.row.id)}
              sx={{
                border: "1px solid #D1E7C7",
                color: "#7CB342",
                width: 28,
                height: 28,
              }}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onDelete(params.row.id, params.row.eventName)}
              sx={{
                border: "1px solid #F2B6B6",
                color: "#E53935",
                width: 28,
                height: 28,
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    [onDelete, onView, onReport]
  );

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#fff",
          border: "1px solid #E5E7EB",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        <DataGrids
          rows={rows}
          columns={columns}
          toolbar
          searchQuery={inputSearch}
          onSearchQueryChange={setInputSearch}
          toolbarShowAddIcon={false}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 20]}
          getRowId={(row: any) => row.id}
          onMobileView={(row: any) => onView(row.id)}
          onMobileDelete={(row: any) => onDelete(row.id, row.eventName)}
          onMobileReport={(row: any) => onReport(row.id)}
          loading={loading}
          sx={{
            height: { xs: "auto", sm: "60vh", md: "60vh" },
            minHeight: 320,
            width: "100%",
            maxWidth: "100%",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#F9FAFB",
              borderBottom: "1px solid #E5E7EB",
            },
          }}
        />
      </Box>
    </>
  );
};

export default AllEventsTable;
